import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEventContext } from '../components/EventContext';
import useProcessing, {
    ProcessingEnvironment,
    ProcessingFlow,
} from '@/src/Processing/components/hooks/useProcessing';
import { Switch } from '@/src/shadComponents/ui/switch';
import { toast } from 'sonner';

/*
 * Processing v2 — the flows list (per the mock): each flow is a row
 * with an inline enable toggle (the classic tab makes you open the
 * editor for that), environment chip, and the step chain in mono —
 * saved-output steps colored. Add/Edit jump to the classic flow
 * editor; flows apply to the activation's (first) gallery.
 */

interface StepInfo {
    name: string;
    saveOutput: boolean;
}

function parseSteps(flowDefinition: string): StepInfo[] {
    try {
        const parsed = JSON.parse(flowDefinition);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((s: any) => ({
            name: s?.name ?? '?',
            saveOutput: !!s?.saveOutput,
        }));
    } catch {
        return [];
    }
}

export default function ProcessingV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const {
        getProcessingFlows,
        getProcessingEnvironments,
        updateProcessingFlow,
        deleteProcessingFlow,
    } = useProcessing();

    const { galleryId, resolved } = useEventContext();
    const [flows, setFlows] = useState<ProcessingFlow[]>([]);
    const [envs, setEnvs] = useState<Record<number, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [busyFlow, setBusyFlow] = useState<number | null>(null);

    useEffect(() => {
        if (!resolved) return;
        let mounted = true;
        const load = async () => {
            try {
                if (galleryId == null) return;
                const [flowRows, envRows] = await Promise.all([
                    getProcessingFlows(galleryId),
                    getProcessingEnvironments(),
                ]);
                if (!mounted) return;
                setFlows(flowRows ?? []);
                setEnvs(
                    Object.fromEntries(
                        (envRows ?? []).map((e: ProcessingEnvironment) => [
                            e.id,
                            e.environment,
                        ]),
                    ),
                );
            } catch {
                /* empty state handles it */
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const toggleFlow = async (flow: ProcessingFlow) => {
        if (busyFlow != null) return;
        setBusyFlow(flow.id);
        const next = !flow.enabled;
        setFlows((rows) =>
            rows.map((f) =>
                f.id === flow.id ? { ...f, enabled: next } : f,
            ),
        );
        const ok = await updateProcessingFlow(
            flow.id,
            flow.flowDefinition,
            flow.environmentId,
            next,
        ).catch(() => null);
        if (!ok) {
            setFlows((rows) =>
                rows.map((f) =>
                    f.id === flow.id ? { ...f, enabled: flow.enabled } : f,
                ),
            );
        }
        setBusyFlow(null);
    };

    const removeFlow = async (flow: ProcessingFlow) => {
        const steps = parseSteps(flow.flowDefinition)
            .map((s) => s.name)
            .join(' → ');
        if (
            !window.confirm(
                `Delete this flow?\n\n${steps || 'empty flow'}\n\nCaptures will no longer run it.`,
            )
        )
            return;
        setBusyFlow(flow.id);
        const ok = await deleteProcessingFlow(flow.id)
            .then(() => true)
            .catch(() => false);
        if (ok) {
            setFlows((rows) => rows.filter((f) => f.id !== flow.id));
        } else {
            toast.error("Couldn't delete the flow — try again.");
        }
        setBusyFlow(null);
    };

    const editorPath = (flowId?: number) =>
        flowId == null
            ? `/v2/activations/${id}/processing/new`
            : `/v2/activations/${id}/processing/${flowId}/edit`;

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-14 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    if (galleryId == null) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-4">Processing</div>
                <p className="text-[13.5px] text-muted-foreground">
                    Processing flows attach to the gallery — this activation
                    has no gallery yet.{' '}
                    <button
                        type="button"
                        onClick={() => navigate(`/activations/${id}/galleries`)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                    >
                        Create one in the classic app →
                    </button>
                </p>
            </main>
        );
    }

    const hasSaved = flows.some((f) =>
        parseSteps(f.flowDefinition).some((s) => s.saveOutput),
    );

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-[18px]">Processing</div>

            <div className="mb-[22px] flex items-start justify-between gap-4">
                <p className="max-w-[52ch] text-[13px] text-muted-foreground">
                    Flows run automatically on every capture — in order, per
                    environment.{' '}
                    <b className="font-semibold text-foreground">
                        {flows.length} flow{flows.length === 1 ? '' : 's'}
                    </b>{' '}
                    on this event's gallery.
                </p>
                <button
                    type="button"
                    onClick={() => navigate(editorPath())}
                    className="cursor-pointer whitespace-nowrap rounded-full border-0 bg-foreground px-[15px] py-2 font-[inherit] text-[13px] font-semibold text-background"
                >
                    Add flow
                </button>
            </div>

            {flows.length === 0 && (
                <p className="py-4 text-[13px] text-muted-foreground">
                    No flows — captures publish unprocessed.
                </p>
            )}

            {flows.map((f, i) => {
                const steps = parseSteps(f.flowDefinition);
                return (
                    <div
                        key={f.id}
                        className={
                            'flex items-center gap-3.5 py-3.5 ' +
                            (i > 0 ? 'border-t border-line' : '')
                        }
                    >
                        <Switch
                            checked={f.enabled}
                            disabled={busyFlow === f.id}
                            onCheckedChange={() => toggleFlow(f)}
                        />
                        <span className="flex-none rounded-full bg-secondary px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                            {envs[f.environmentId] ?? `env ${f.environmentId}`}
                        </span>
                        <span
                            className={
                                'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[12px] ' +
                                (f.enabled ? '' : 'text-faint')
                            }
                        >
                            {steps.length === 0 && (
                                <span className="text-faint">empty flow</span>
                            )}
                            {steps.map((s, si) => (
                                <React.Fragment key={si}>
                                    {si > 0 && (
                                        <span className="mx-1 text-faint">
                                            →
                                        </span>
                                    )}
                                    <span
                                        className={
                                            s.saveOutput && f.enabled
                                                ? 'text-brand-ink'
                                                : ''
                                        }
                                    >
                                        {s.name}
                                    </span>
                                </React.Fragment>
                            ))}
                        </span>
                        <span className="flex-none text-[11.5px] tabular-nums text-faint">
                            {steps.length} step{steps.length === 1 ? '' : 's'}
                        </span>
                        <span className="ml-auto flex flex-none items-center gap-2.5">
                            <button
                                type="button"
                                onClick={() => navigate(editorPath(f.id))}
                                className="cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                            >
                                Edit →
                            </button>
                            <button
                                type="button"
                                title="Delete flow"
                                disabled={busyFlow === f.id}
                                onClick={() => removeFlow(f)}
                                className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent text-faint hover:bg-secondary hover:text-attention"
                            >
                                <svg
                                    viewBox="0 0 20 20"
                                    className="h-[13px] w-[13px]"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M4.5 6h11M8 6V4.5h4V6M6 6l.7 9.5h6.6L14 6M8.5 9v4M11.5 9v4" />
                                </svg>
                            </button>
                        </span>
                    </div>
                );
            })}

            <div className="mt-3.5 flex items-baseline gap-4 text-[12px] text-faint">
                <span>Flows apply to this event's gallery.</span>
                <button
                    type="button"
                    onClick={() =>
                        navigate(`/v2/activations/${id}/workflows`)
                    }
                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                >
                    Automation workflows →
                </button>
            </div>
            {hasSaved && (
                <div className="mt-6 flex gap-4 border-t border-line pt-3.5 text-[11.5px] text-faint">
                    <span className="flex items-center gap-1.5">
                        <span className="h-[7px] w-[7px] rounded-full bg-brand-ink" />
                        colored step = output saved to the gallery
                    </span>
                </div>
            )}
        </main>
    );
}
